import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { AnswerResult, QuizQuestionView } from '@/types/api';

/**
 * QuizQuestionCard — one question in the quiz's "asking" phase.
 *
 * Matches the state matrix from quiz spec §4.3:
 *   - Idle: white, orange-ink letter bubble
 *   - Hover: warm hover, same bubble
 *   - Correct: green fill + check
 *   - Picked-wrong: red fill + X
 *   - Unpicked-wrong: dimmed idle (#8A7F72 text)
 *   - Unpicked-correct (after wrong pick): green-soft with dashed border
 *
 * Behaviour:
 *   - Commit-on-tap: once any option is picked, every other option
 *     freezes. No "confirm" step. Changing your mind requires "Try
 *     again" from the score screen.
 *   - After a wrong pick, the correct option is always highlighted.
 *   - Advancing is always manual: the user must click "Next question"
 *     (or "Finish quiz" on the last question) to move on. No auto-advance
 *     timer — readers control the pace and can take as long as they need.
 */
export interface QuizQuestionCardProps {
  questionIndex: number;
  totalQuestions: number;
  question: QuizQuestionView;
  picked: string | null;
  result: AnswerResult | null;
  onSelect: (questionId: string, optionId: string) => void;
  onAdvance: () => void;
}

export function QuizQuestionCard({
  questionIndex,
  totalQuestions,
  question,
  picked,
  result,
  onSelect,
  onAdvance,
}: QuizQuestionCardProps) {
  return (
    <section>
      <div
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '12px',
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: '#8A7F72',
        }}
      >
        Question {questionIndex + 1} of {totalQuestions}
      </div>

      <h2
        style={{
          fontFamily: '"DM Serif Display", serif',
          fontWeight: 400,
          fontSize: '30px',
          lineHeight: 1.15,
          color: '#1F1B16',
          margin: '6px 0 18px',
        }}
      >
        {question.prompt}
      </h2>

      <div
        role="radiogroup"
        aria-label={`Answers for question ${questionIndex + 1}`}
        className="flex flex-col gap-2"
      >
        {question.options.map((opt, i) => {
          const letter = String.fromCharCode(65 + i); // A, B, C, D
          const isPicked = picked === opt.id;
          const isCorrectAnswer = result ? result.correctOptionId === opt.id : false;
          const hasResult = !!result;
          const isWrongPick = hasResult && isPicked && !result!.isCorrect;
          const isDimmed = hasResult && !isPicked && !isCorrectAnswer;
          const isRevealedCorrect = hasResult && !result!.isCorrect && isCorrectAnswer && !isPicked;

          return (
            <OptionButton
              key={opt.id}
              letter={letter}
              text={opt.text}
              emoji={opt.emoji ?? null}
              isPicked={isPicked}
              isCorrectAnswer={isCorrectAnswer}
              hasResult={hasResult}
              isWrongPick={isWrongPick}
              isDimmed={isDimmed}
              isRevealedCorrect={isRevealedCorrect}
              disabled={!!picked}
              onClick={() => onSelect(question.id, opt.id)}
            />
          );
        })}
      </div>

      {/* Next button — always rendered so keyboard users can focus it
          pre-selection; disabled until a pick is made. Clicking it is
          the only way to advance — there is no auto-advance timer. */}
      <div className="mt-5 flex justify-end">
        <button
          type="button"
          disabled={!picked}
          onClick={onAdvance}
          className={cn(
            'inline-flex items-center gap-2 rounded-full transition-colors',
            picked ? 'cursor-pointer' : 'cursor-not-allowed',
          )}
          style={{
            backgroundColor: picked ? '#2E8A8A' : '#EFE5D2',
            color: picked ? '#FFFFFF' : '#8A7F72',
            padding: '10px 18px',
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            fontWeight: 600,
            border: 'none',
          }}
        >
          {questionIndex + 1 === totalQuestions ? 'Finish quiz' : 'Next question'}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

interface OptionButtonProps {
  letter: string;
  text: string;
  emoji: string | null;
  isPicked: boolean;
  isCorrectAnswer: boolean;
  hasResult: boolean;
  isWrongPick: boolean;
  isDimmed: boolean;
  isRevealedCorrect: boolean;
  disabled: boolean;
  onClick: () => void;
}

function OptionButton(props: OptionButtonProps) {
  const {
    letter,
    text,
    emoji,
    isPicked,
    isCorrectAnswer,
    hasResult,
    isWrongPick,
    isDimmed,
    isRevealedCorrect,
    disabled,
    onClick,
  } = props;

  // Determine visual state per spec §4.3.
  let bg = '#FFFFFF';
  let border = '#EFE5D2 1px solid';
  let textColor = '#1F1B16';
  let bubbleBg = '#FCEFDD';
  let bubbleText = '#B85A1E';
  let bubbleContent: string | JSX.Element = letter;

  if (hasResult) {
    if (isPicked && isCorrectAnswer) {
      bg = '#E7F5E7';
      border = '#4CAF50 2px solid';
      bubbleBg = '#4CAF50';
      bubbleText = '#FFFFFF';
      bubbleContent = '✓';
    } else if (isWrongPick) {
      bg = '#FCEAE6';
      border = '#D94F3A 2px solid';
      bubbleBg = '#D94F3A';
      bubbleText = '#FFFFFF';
      bubbleContent = '✕';
    } else if (isRevealedCorrect) {
      bg = 'rgba(231, 245, 231, 0.6)';
      border = '#9FCFA0 1.5px dashed';
      bubbleBg = '#4CAF50';
      bubbleText = '#FFFFFF';
      bubbleContent = '✓';
    } else if (isDimmed) {
      textColor = '#8A7F72';
    }
  }

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isPicked}
      disabled={disabled && !isPicked}
      onClick={onClick}
      className={cn(
        'group flex items-center text-left transition-colors',
        !disabled && 'hover:bg-[#FDF5E9]',
      )}
      style={{
        backgroundColor: bg,
        border,
        borderRadius: '14px',
        padding: '14px 16px',
        gap: '14px',
        cursor: disabled && !isPicked ? 'default' : 'pointer',
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          borderRadius: '999px',
          backgroundColor: bubbleBg,
          color: bubbleText,
          fontFamily: 'Inter, sans-serif',
          fontSize: '13px',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {bubbleContent}
      </span>
      <span
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '15px',
          color: textColor,
          lineHeight: 1.4,
        }}
      >
        {emoji ? <span style={{ marginRight: '6px' }}>{emoji}</span> : null}
        {text}
      </span>
    </button>
  );
}
